import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { useDiasDaGrade, AvisoDiasDaGrade } from '../../components/GradeHoraria'
import { useNavigate } from 'react-router-dom'
import { IndexAllAttendance, getSchoolYear, getSchoolDays } from '../../Api'
import { SlActionUndo } from 'react-icons/sl'
import { FiCheckCircle, FiXCircle, FiPlus, FiCalendar, FiAlertTriangle } from 'react-icons/fi'
import {
    Container,
    ContainerDivs,
    Header,
    BackButton,
    HeaderInfo,
    Title,
    Subtitle,
    HeaderBadge,
    ProgressCard,
    ProgressTop,
    ProgressLabel,
    ProgressValue,
    ProgressTrack,
    ProgressFill,
    Toolbar,
    DateInput,
    ChipButton,
    AddButton,
    Section,
    SectionHeader,
    SectionTitle,
    CountPill,
    List,
    EmpDone,
    EmpPending,
    EmpWarn,
    WarnTag,
    DateText,
    WeekdayTag,
    EmptyState,
    ShowMoreButton,
    SkeletonCard,
    SkeletonLine,
    ToGoBack,
    SignMessageButtonText,
    SignMessageButtonTextBold,
    ModalOverlay,
    ModalContent,
    ModalButton
} from './style'

// ── helpers fora do componente ─────────────────────────────────────
const safeParse = (raw) => {
    try { return raw ? JSON.parse(raw) : null } catch { return null }
}

// "dd/mm/yyyy" a partir de partes numéricas
const fmtDate = (day, month, year) =>
    `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`

// "yyyy-mm-dd" (input date) → "dd/mm/yyyy"
const isoToBr = (iso) => {
    if (!iso) return ''
    const [year, month, day] = iso.split('-')
    return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`
}

// dia da semana abreviado a partir de "dd/mm/yyyy"
const weekday = (br) => {
    const [d, m, y] = br.split('/').map(Number)
    const dt = new Date(y, m - 1, d)
    const dias = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado']
    return dias[dt.getDay()] || ''
}

const COLLAPSED_LIMIT = 5

const AllAttendance = () => {
    const navigate = useNavigate()
    const mountedRef = useRef(true)

    const [loading, setLoading] = useState(true)
    const [attendance, setAttendance] = useState([])
    const [schoolDays, setSchoolDays] = useState([])
    const [searchDate, setSearchDate] = useState('')

    const [id_teacher, setId_teacher] = useState('')
    // grade horária: só os dias em que o professor tem aula (+ sábados letivos)
    const grade = useDiasDaGrade(sessionStorage.getItem('class-info'), (id_teacher && (id_teacher._id || id_teacher)) || '')
    const [regentTeacher, setRegentTeacher] = useState(null)
    const [physicalEducation, setPhysicalEducation] = useState(null)

    const [showModal, setShowModal] = useState(false)
    const [showAllDone, setShowAllDone] = useState(false)

    useEffect(() => {
        mountedRef.current = true
        return () => { mountedRef.current = false }
    }, [])

    useEffect(() => {
        (async () => {
            setLoading(true)

            const idSchool = safeParse(sessionStorage.getItem('id-school'))
            const idClass = sessionStorage.getItem('class-info')
            const idTeacher = safeParse(localStorage.getItem('Id_employee')) || ''
            const classRegentTeacher = safeParse(sessionStorage.getItem('classRegentTeacher'))
            const physicalEducationTeacher = safeParse(sessionStorage.getItem('physicalEducationTeacher'))

            setId_teacher(idTeacher)
            setRegentTeacher(classRegentTeacher)
            setPhysicalEducation(physicalEducationTeacher)

            try {
                // ano letivo primeiro (necessário para os dias letivos)
                const yearRes = await getSchoolYear(idSchool)
                if (!mountedRef.current) return
                const year = yearRes?.data?.data

                // dias letivos + chamadas já realizadas em PARALELO
                const [schoolDaysRes, attRes] = await Promise.all([
                    getSchoolDays(idSchool, year),
                    IndexAllAttendance({ id_class: idClass })
                ])
                if (!mountedRef.current) return

                setSchoolDays(schoolDaysRes?.data?.schoolDays || [])
                setAttendance(attRes?.data?.data || [])
            } catch (err) {
                console.error('Erro ao carregar frequências:', err)
            } finally {
                if (mountedRef.current) setLoading(false)
            }

            // limpa o contexto de uma chamada anterior
            sessionStorage.removeItem('selectedDate')
            sessionStorage.removeItem('day')
            sessionStorage.removeItem('month')
            sessionStorage.removeItem('year')
        })()
    }, [])

    // ── filtro por tipo de professor (memoizado) ────────────────────
    const filteredByTeacherType = useMemo(() => {
        return attendance.filter(item => {
            if (!item) return false
            const isPE = item.isPhysicalEducation
            if (physicalEducation === id_teacher) return isPE === true
            if (regentTeacher === id_teacher) return isPE === false || isPE === undefined
            return true
        })
    }, [attendance, physicalEducation, regentTeacher, id_teacher])

    // ── chamadas únicas, ordenadas da mais recente (memoizado) ─────
    const uniqueAttendance = useMemo(() => {
        return [
            ...new Map(
                filteredByTeacherType
                    .slice()
                    .sort((a, b) =>
                        new Date(b.year, b.month - 1, b.day) - new Date(a.year, a.month - 1, a.day))
                    .map((item) => {
                        const dateKey = fmtDate(item.day, item.month, item.year)
                        return [dateKey, { ...item, dateKey }]
                    })
            ).values()
        ]
    }, [filteredByTeacherType])

    // ── dias letivos formatados + dias sem chamada (memoizado) ─────
    const { schoolDaysFormatted, daysWithoutAttendance } = useMemo(() => {
        const formatted = schoolDays.map(d => {
            const [year, month, day] = d.split('-')
            return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`
        })
        const doneSet = new Set(uniqueAttendance.map(i => i.dateKey))
        return {
            schoolDaysFormatted: formatted,
            daysWithoutAttendance: formatted.filter(day => !doneSet.has(day) && grade.permitido(day))
        }
    }, [schoolDays, uniqueAttendance, grade])

    // conjunto de dias letivos (para marcar chamadas feitas fora deles)
    const schoolDaysSet = useMemo(() => new Set(schoolDaysFormatted), [schoolDaysFormatted])
    const isNaoLetivo = useCallback(
        (dateKey) => schoolDaysSet.size > 0 && !schoolDaysSet.has(dateKey),
        [schoolDaysSet]
    )

    // ── aplicação da busca por data (memoizado) ────────────────────
    const filteredDone = useMemo(() => {
        if (!searchDate) return uniqueAttendance
        const formatted = isoToBr(searchDate)
        return uniqueAttendance.filter(item => item.dateKey === formatted)
    }, [searchDate, uniqueAttendance])

    const filteredPending = useMemo(() => {
        if (!searchDate) return daysWithoutAttendance
        const formatted = isoToBr(searchDate)
        return daysWithoutAttendance.filter(date => date === formatted)
    }, [searchDate, daysWithoutAttendance])

    // progresso do total de dias letivos
    const progress = useMemo(() => {
        const total = schoolDaysFormatted.filter(d => grade.permitido(d)).length
        const done = uniqueAttendance.length
        const pct = total > 0 ? Math.round((done / total) * 100) : 0
        return { total, done, pct }
    }, [schoolDaysFormatted, uniqueAttendance, grade])

    // ── handlers ─────────────────────────────────────────────────────
    const goBack = useCallback(() => navigate(-1), [navigate])

    const handleAddAttendance = useCallback(() => {
        navigate('/attendance')
    }, [navigate])

    const handleClickAttendance = useCallback((item) => {
        if (item.id_teacher !== id_teacher) {
            setShowModal(true)
            return
        }
        sessionStorage.setItem('selectedDate', item.dateKey)
        sessionStorage.setItem('day', item.day)
        sessionStorage.setItem('month', item.month)
        sessionStorage.setItem('year', item.year)
        navigate('/attendance')
    }, [id_teacher, navigate])

    const handleClickNoAttendance = useCallback((date) => {
        const [day, month, year] = date.split('/')
        sessionStorage.setItem('selectedDate', date)
        sessionStorage.setItem('day', JSON.stringify(Number(day)))
        sessionStorage.setItem('month', JSON.stringify(Number(month)))
        sessionStorage.setItem('year', JSON.stringify(Number(year)))
        navigate('/attendance')
    }, [navigate])

    const setToday = useCallback(() => {
        const now = new Date()
        const iso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
        setSearchDate(iso)
    }, [])

    const clearSearch = useCallback(() => setSearchDate(''), [])

    // ── skeleton ────────────────────────────────────────────────────
    if (loading) {
        return (
            <Container>
                <ContainerDivs>
                    <Header>
                        <HeaderInfo><Title>Frequências</Title></HeaderInfo>
                    </Header>
                    <SkeletonCard>
                        <SkeletonLine $w="40%" $h="18px" />
                        <SkeletonLine $w="100%" $h="10px" />
                    </SkeletonCard>
                    {[1, 2].map(i => (
                        <SkeletonCard key={i}>
                            <SkeletonLine $w="50%" $h="16px" />
                            <SkeletonLine $w="100%" />
                            <SkeletonLine $w="100%" />
                        </SkeletonCard>
                    ))}
                </ContainerDivs>
            </Container>
        )
    }

    const visibleDone = showAllDone ? filteredDone : filteredDone.slice(0, COLLAPSED_LIMIT)

    return (
        <Container>
            <ContainerDivs>
                <Header>
                    <BackButton onClick={goBack} aria-label="Voltar">
                        <SlActionUndo size={20} />
                    </BackButton>
                    <HeaderInfo>
                        <Title>Frequências</Title>
                        <Subtitle>Chamada diária da turma</Subtitle>
                    </HeaderInfo>
                    {filteredPending.length > 0 && (
                        <HeaderBadge $tone="pending">
                            {filteredPending.length} {filteredPending.length === 1 ? 'pendente' : 'pendentes'}
                        </HeaderBadge>
                    )}
                </Header>

                {/* PROGRESSO (assinatura) */}
                <ProgressCard>
                    <ProgressTop>
                        <ProgressLabel>Frequências do ano letivo</ProgressLabel>
                        <ProgressValue>
                            {progress.done}<small>de {progress.total} dias</small>
                        </ProgressValue>
                    </ProgressTop>
                    <ProgressTrack>
                        <ProgressFill $pct={progress.pct} />
                    </ProgressTrack>
                </ProgressCard>

                {/* BUSCA + NOVA FREQUÊNCIA */}
                <Toolbar>
                    <DateInput
                        type="date"
                        value={searchDate}
                        onChange={(e) => setSearchDate(e.target.value)}
                        aria-label="Buscar frequência por data"
                    />
                    <ChipButton type="button" onClick={setToday}>Hoje</ChipButton>
                    <ChipButton type="button" onClick={clearSearch} disabled={!searchDate}>
                        Limpar
                    </ChipButton>
                    <AddButton onClick={handleAddAttendance}>
                        <FiPlus size={16} /> Nova frequência
                    </AddButton>
                </Toolbar>

                {/* REALIZADAS */}
                <Section>
                    <SectionHeader $tone="done">
                        <SectionTitle $tone="done">
                            <FiCheckCircle size={16} /> Frequências realizadas
                        </SectionTitle>
                        <CountPill $tone="done">{filteredDone.length}</CountPill>
                    </SectionHeader>
                    {filteredDone.length > 0 ? (
                        <>
                            <List>
                                {visibleDone.map((item, idx) => (
                                    isNaoLetivo(item.dateKey) ? (
                                        <EmpWarn
                                            key={`${item.dateKey}-${idx}`}
                                            onClick={() => handleClickAttendance(item)}
                                            title="Frequência lançada em um dia que não está cadastrado como dia letivo. Verifique a data."
                                        >
                                            <DateText>
                                                <FiCalendar size={16} />
                                                {item.dateKey}
                                                <WeekdayTag>{weekday(item.dateKey)}</WeekdayTag>
                                            </DateText>
                                            <WarnTag>
                                                <FiAlertTriangle size={20} /> Verificar se foi dia letivo
                                            </WarnTag>
                                        </EmpWarn>
                                    ) : (
                                        <EmpDone key={`${item.dateKey}-${idx}`} onClick={() => handleClickAttendance(item)}>
                                            <DateText>
                                                <FiCalendar size={16} />
                                                {item.dateKey}
                                                <WeekdayTag>{weekday(item.dateKey)}</WeekdayTag>
                                            </DateText>
                                            <FiCheckCircle size={20} color="#1d8b5a" />
                                        </EmpDone>
                                    )
                                ))}
                            </List>
                            {filteredDone.length > COLLAPSED_LIMIT && (
                                <ShowMoreButton onClick={() => setShowAllDone(prev => !prev)}>
                                    {showAllDone
                                        ? 'Ver menos'
                                        : `Ver mais (${filteredDone.length - COLLAPSED_LIMIT})`}
                                </ShowMoreButton>
                            )}
                        </>
                    ) : (
                        <EmptyState>
                            {searchDate
                                ? 'Nenhuma frequência registrada na data buscada.'
                                : 'Nenhuma frequência registrada ainda.'}
                        </EmptyState>
                    )}
                </Section>

                {/* PENDENTES */}
                <Section>
                    <AvisoDiasDaGrade grade={grade} />
                    <SectionHeader $tone="pending">
                        <SectionTitle $tone="pending">
                            <FiXCircle size={16} /> Dias letivos sem frequência
                        </SectionTitle>
                        <CountPill $tone="pending">{filteredPending.length}</CountPill>
                    </SectionHeader>
                    {filteredPending.length > 0 ? (
                        <List>
                            {filteredPending.map((day, idx) => (
                                <EmpPending key={`${day}-${idx}`} onClick={() => handleClickNoAttendance(day)}>
                                    <DateText>
                                        <FiCalendar size={16} />
                                        {day}
                                        <WeekdayTag>{weekday(day)}</WeekdayTag>
                                    </DateText>
                                    <FiXCircle size={20} color="#c0392b" />
                                </EmpPending>
                            ))}
                        </List>
                    ) : (
                        <EmptyState>
                            <strong>Tudo em dia! 🎉</strong>
                            {searchDate
                                ? 'Nenhuma pendência para a data buscada.'
                                : 'Não há dias letivos sem frequência.'}
                        </EmptyState>
                    )}
                </Section>

                <ToGoBack onClick={goBack} role="button" tabIndex={0}>
                    <SignMessageButtonText>Voltar para a</SignMessageButtonText>
                    <SignMessageButtonTextBold>Turma</SignMessageButtonTextBold>
                </ToGoBack>

                {/* MODAL: chamada de outro professor */}
                {showModal && (
                    <ModalOverlay onClick={() => setShowModal(false)}>
                        <ModalContent onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                            <h3>⚠️ Acesso restrito</h3>
                            <p>Esta frequência foi registrada por outro professor e não pode ser aberta aqui.</p>
                            <ModalButton onClick={() => setShowModal(false)}>Entendi</ModalButton>
                        </ModalContent>
                    </ModalOverlay>
                )}
            </ContainerDivs>
        </Container>
    )
}

export default AllAttendance